import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiTrendingUp, FiDollarSign, FiUsers, FiClock } = FiIcons;

const DataChart = () => {
  const marketData = [
    { year: '2020', value: 15, label: '$15B', height: 25 },
    { year: '2021', value: 25, label: '$25B', height: 42 },
    { year: '2022', value: 32, label: '$32B', height: 58 },
    { year: '2023', value: 38, label: '$38B', height: 75 },
    { year: '2024', value: 43, label: '$43B', height: 100 }
  ];

  const comparisonData = [
    {
      metric: 'Initial Investment',
      traditional: 350000,
      ghost: 100000,
      icon: FiDollarSign,
      unit: '$'
    },
    {
      metric: 'Time to Launch',
      traditional: 180,
      ghost: 30,
      icon: FiClock,
      unit: ' days'
    },
    {
      metric: 'Monthly Overhead',
      traditional: 25000,
      ghost: 8000,
      icon: FiTrendingUp,
      unit: '$'
    },
    {
      metric: 'Staff Required',
      traditional: 15,
      ghost: 5,
      icon: FiUsers,
      unit: ' people'
    }
  ];

  return (
    <section className="py-20 bg-gradient-to-b from-slate-900 to-purple-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            The Numbers Don't Lie
          </h2>
          <p className="text-xl text-gray-300 max-w-2xl mx-auto">
            Ghost kitchens are reshaping the food industry with explosive growth and proven results
          </p>
        </motion.div>

        {/* Enhanced Market Growth Chart */}
        <div className="mb-20">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20"
          >
            <h3 className="text-2xl font-bold text-white mb-8 text-center">
              Ghost Kitchen Market Growth (2020-2024)
            </h3>
            <div className="relative h-80 bg-gradient-to-t from-slate-800/50 to-transparent rounded-lg p-6">
              {/* Y-axis labels */}
              <div className="absolute left-2 top-6 bottom-12 flex flex-col justify-between text-gray-400 text-sm">
                <span>$50B</span>
                <span>$40B</span>
                <span>$30B</span>
                <span>$20B</span>
                <span>$10B</span>
                <span>$0B</span>
              </div>
              
              {/* Grid lines */}
              <div className="absolute left-12 right-6 top-6 bottom-12">
                {[0, 20, 40, 60, 80, 100].map((line, index) => (
                  <div
                    key={index}
                    className="absolute w-full border-t border-gray-600/30"
                    style={{ bottom: `${line}%` }}
                  />
                ))}
              </div>
              
              {/* Bars */}
              <div className="absolute left-12 right-6 bottom-12 top-6 flex items-end justify-between">
                {marketData.map((data, index) => (
                  <motion.div
                    key={data.year}
                    initial={{ height: 0, opacity: 0 }}
                    whileInView={{ 
                      height: `${data.height}%`,
                      opacity: 1
                    }}
                    transition={{ 
                      duration: 1.2, 
                      delay: index * 0.3,
                      ease: "easeOut"
                    }}
                    className="flex flex-col items-center flex-1 mx-2 group relative"
                  >
                    {/* Bar */}
                    <div
                      className="w-full max-w-16 bg-gradient-to-t from-purple-600 via-purple-500 to-pink-500 rounded-t-lg relative shadow-lg group-hover:shadow-purple-500/50 transition-all duration-300 cursor-pointer"
                      style={{ minHeight: '20px' }}
                    >
                      {/* Glow effect */}
                      <div className="absolute inset-0 bg-gradient-to-t from-purple-400 to-pink-400 rounded-t-lg opacity-0 group-hover:opacity-30 transition-opacity duration-300" />
                      
                      {/* Value tooltip */}
                      <div className="absolute -top-12 left-1/2 transform -translate-x-1/2 bg-black/90 text-white px-3 py-2 rounded-lg text-sm font-bold opacity-0 group-hover:opacity-100 transition-all duration-300 whitespace-nowrap z-10">
                        {data.label}
                        <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-black/90" />
                      </div>
                    </div>
                    
                    {/* Year label */}
                    <div className="text-white text-sm mt-3 font-semibold group-hover:text-purple-300 transition-colors">
                      {data.year}
                    </div>
                  </motion.div>
                ))}
              </div>
              
              {/* Trend line */}
              <svg className="absolute left-12 right-6 top-6 bottom-12 w-full h-full pointer-events-none">
                <motion.path
                  initial={{ pathLength: 0, opacity: 0 }}
                  whileInView={{ pathLength: 1, opacity: 0.6 }}
                  transition={{ duration: 2, delay: 1.5 }}
                  d="M 10% 75% Q 30% 65%, 50% 45% T 90% 5%"
                  stroke="url(#trendGradient)"
                  strokeWidth="3"
                  fill="none"
                  strokeDasharray="5,5"
                />
                <defs>
                  <linearGradient id="trendGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor="#10B981" />
                    <stop offset="100%" stopColor="#06D6A0" />
                  </linearGradient>
                </defs>
              </svg>
            </div>
            <div className="text-center mt-4 text-gray-300 flex items-center justify-center gap-2">
              <SafeIcon icon={FiTrendingUp} className="text-green-400" />
              Market Size Growth Trajectory
            </div>
          </motion.div>
        </div>

        {/* Comparison Chart */}
        <div className="grid md:grid-cols-2 gap-8">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20"
          >
            <h3 className="text-2xl font-bold text-white mb-6 text-center">
              Traditional vs Ghost Kitchen
            </h3>
            <div className="space-y-6">
              {comparisonData.map((item, index) => (
                <div key={index} className="space-y-2">
                  <div className="flex items-center text-white mb-2">
                    <SafeIcon icon={item.icon} className="mr-2 text-purple-400" />
                    <span className="font-semibold">{item.metric}</span>
                  </div>
                  
                  {/* Traditional Restaurant Bar */}
                  <div className="relative">
                    <div className="flex justify-between items-center mb-1">
                      <span className="text-red-300 text-sm">Traditional</span>
                      <span className="text-red-300 text-sm">
                        {item.unit === '$' ? '$' : ''}{item.traditional.toLocaleString()}{item.unit !== '$' ? item.unit : ''}
                      </span>
                    </div>
                    <div className="w-full bg-gray-700 rounded-full h-3 overflow-hidden">
                      <motion.div
                        initial={{ width: 0 }}
                        whileInView={{ width: '100%' }}
                        transition={{ duration: 1, delay: index * 0.1 }}
                        className="bg-gradient-to-r from-red-600 to-red-400 h-3 rounded-full shadow-lg"
                      />
                    </div>
                  </div>

                  {/* Ghost Kitchen Bar */}
                  <div className="relative">
                    <div className="flex justify-between items-center mb-1">
                      <span className="text-green-300 text-sm">Ghost Kitchen</span>
                      <span className="text-green-300 text-sm">
                        {item.unit === '$' ? '$' : ''}{item.ghost.toLocaleString()}{item.unit !== '$' ? item.unit : ''}
                      </span>
                    </div>
                    <div className="w-full bg-gray-700 rounded-full h-3 overflow-hidden">
                      <motion.div
                        initial={{ width: 0 }}
                        whileInView={{ width: `${(item.ghost / item.traditional) * 100}%` }}
                        transition={{ duration: 1, delay: index * 0.1 + 0.2 }}
                        className="bg-gradient-to-r from-green-500 to-emerald-400 h-3 rounded-full shadow-lg"
                      />
                    </div>
                  </div>
                  
                  {/* Savings indicator */}
                  <div className="text-center mt-2">
                    <span className="text-green-400 text-xs font-bold bg-green-400/20 px-2 py-1 rounded">
                      {Math.round(((item.traditional - item.ghost) / item.traditional) * 100)}% savings
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20"
          >
            <h3 className="text-2xl font-bold text-white mb-6 text-center">
              Success Metrics
            </h3>
            <div className="space-y-6">
              <motion.div 
                initial={{ scale: 0 }}
                whileInView={{ scale: 1 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="text-center"
              >
                <div className="text-5xl font-bold text-green-400 mb-2">92%</div>
                <div className="text-gray-300">Success Rate in First Year</div>
              </motion.div>
              <motion.div 
                initial={{ scale: 0 }}
                whileInView={{ scale: 1 }}
                transition={{ duration: 0.5, delay: 0.4 }}
                className="text-center"
              >
                <div className="text-5xl font-bold text-purple-400 mb-2">6</div>
                <div className="text-gray-300">Average Break-even (Months)</div>
              </motion.div>
              <motion.div 
                initial={{ scale: 0 }}
                whileInView={{ scale: 1 }}
                transition={{ duration: 0.5, delay: 0.6 }}
                className="text-center"
              >
                <div className="text-5xl font-bold text-pink-400 mb-2">300%</div>
                <div className="text-gray-300">Average ROI in Year 2</div>
              </motion.div>
              <div className="bg-gradient-to-r from-purple-600/30 to-pink-600/30 rounded-lg p-4 mt-6">
                <div className="text-center text-white">
                  <div className="text-lg font-semibold mb-2">Industry Leader Quote</div>
                  <div className="text-sm italic">
                    "Ghost kitchens represent the biggest shift in food service since the invention of the drive-thru."
                  </div>
                  <div className="text-xs mt-2 opacity-75">- Restaurant Industry Report 2024</div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default DataChart;